/**
 * Data adapters for ItemTile
 * Convert raw game types (FishStack, EquipmentDefinition) into normalized ItemTileData
 */

import { FishStack, Quality, EquipmentDefinition, QualityNames, QualityColors } from '@/types';
import { getFishDefinition, QualityMultipliers } from '@/data';
import { SLOT_CONFIG } from '@/data/equipment/slotConfig';

export interface StatLine {
  label: string;
  value: string;
  color?: string; // Tailwind text color class
}

export interface ItemTileData {
  icon: string;
  name: string;
  subtitle: string;
  subtitleColor?: string; // hex color for quality tinting
  quantity?: number; // shown as ×N on subtitle line
  stats: StatLine[];
  badge?: string; // "EQ", "🔒"
  badgeColor?: string; // Tailwind text color class
}

/**
 * Convert a fish stack into tile display data
 */
export function fishStackToTileData(
  stack: FishStack,
  isProtected: boolean,
): ItemTileData {
  const def = getFishDefinition(stack.definitionId);
  const quality = stack.quality as Quality;
  const multiplier = QualityMultipliers[quality];
  const value = Math.round(def.baseValue * multiplier);

  return {
    icon: def.emoji || def.icon || '🐟',
    name: def.name,
    subtitle: QualityNames[quality],
    subtitleColor: QualityColors[quality],
    quantity: stack.count,
    stats: [
      { label: 'Val', value: `${value}g`, color: 'text-yellow-400' },
      { label: '×', value: `${multiplier}`, color: 'text-blue-400' },
    ],
    badge: isProtected ? '🔒' : undefined,
    badgeColor: isProtected ? 'text-yellow-500' : undefined,
  };
}

/**
 * Convert an equipment definition into tile display data
 */
export function equipmentToTileData(
  def: EquipmentDefinition,
  opts: {
    isEquipped?: boolean;
    availableCount?: number;
    totalOwned?: number;
  } = {},
): ItemTileData {
  const slotMeta = SLOT_CONFIG[def.slot];
  const stats: StatLine[] = [];

  if (def.stats.fishingSpeed) {
    stats.push({
      label: 'Spd',
      value: `+${Math.round(def.stats.fishingSpeed * 100)}%`,
      color: 'text-green-400',
    });
  }
  if (def.stats.catchChance) {
    stats.push({
      label: 'Catch',
      value: `+${Math.round(def.stats.catchChance * 100)}%`,
      color: 'text-blue-400',
    });
  }
  if (def.stats.qualityBonus) {
    stats.push({
      label: 'Qual',
      value: `+${Math.round(def.stats.qualityBonus * 100)}%`,
      color: 'text-purple-400',
    });
  }

  return {
    icon: def.icon || slotMeta.icon,
    name: def.name,
    subtitle: `${slotMeta.label} T${def.tier}`,
    quantity: opts.totalOwned,
    stats,
    badge: opts.isEquipped ? 'EQ' : undefined,
    badgeColor: opts.isEquipped ? 'text-green-400' : undefined,
  };
}
